import * as React from "react";
import {
  Chart,
  ChartTitle,
  ChartSeries,
  ChartSeriesItem,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartLegend,
} from "@progress/kendo-react-charts";
import "../L2L_Dashboard.scss";
import PropTypes from "prop-types";

const RevenueChart = ({ revenueChartData }) => {
  if (!revenueChartData || Object.keys(revenueChartData).length === 0) {
    return null;
  }

  const categories = [
    "Initial Contact",
    "Stakeholders Identified",
    "Product/Process",
    "Deal Nurturing",
    "Terms/Contract",
    "Revenue Booked",
    "Total",
  ];

  const categoryMap = {
    "Initial Contact": "InitialContact",
    "Stakeholders Identified": "StakeholdersIdentified",
    "Product/Process": "Product_Process",
    "Deal Nurturing": "DealNurturing",
    "Terms/Contract": "Terms_Contract",
    "Revenue Booked": "RevenueBooked",
    Total: "Total",
  };

  const extractAmounts = (key) =>
    categories.map((category) => revenueChartData[categoryMap[category]][key]);

  const chartData = {
    ARR: { name: "ARR", data: extractAmounts("ARRAmount"), color: "#FFB90D" },
    ServicesRevenue: {
      name: "Services Revenue",
      data: extractAmounts("ServicesAmount"),
      color: "#0099BC",
    },
    WeightedRevenue: {
      name: "Weighted Revenue",
      data: extractAmounts("WeighedAmount"),
      color: "#E84856",
    },
    NumberOfDeals: {
      name: "Number of Deals",
      data: extractAmounts("NumberOfDeals"),
      color: "#8764B8",
      axis: "dealsAxis",
    },
  };

  const chartSeriesItems = Object.values(chartData).map((item) => (
    <ChartSeriesItem
      key={item.name}
      type="column"
      name={item.name}
      data={item.data}
      axis={item.axis || "amountAxis"}
      labels={{
        visible: ({ value }) => value !== 0,
        content: ({ value }) => {
          if (value >= 1000) {
            const formattedValue = (value / 1000)
              .toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 1 });
            return `${formattedValue}k`;
          }
          return value.toLocaleString("en-US");
        },
        format: item.axis === "dealsAxis" ? "N0" : "${0:n0}",
        rotation: item.axis === "dealsAxis" ? "auto" : undefined,
        position: item.axis === "dealsAxis" ? "outsideEnd" : undefined,
        background: item.axis === "dealsAxis" ? "none" : undefined,
        color: item.axis === "dealsAxis" ? "#FFF" : undefined,
      }}
      color={item.color}
      gap={2.5}
      spacing={0.6}
      style={{outline: "none"}}
    />
  ));

  return (
    <Chart
      className="L2LDashboardContainer"
      transitions={false}
      style={{ height: "500px" }}
      data-testid="revenue-chart"
    >
      <ChartTitle
        text="Sales Pipeline"
        align="left"
        font='600 16.002px'
        margin={{ bottom: 20 }}
        padding={{ bottom: 10 }}
      />

      <ChartLegend
        position="bottom"
        orientation="horizontal"
        margin={{ top: 20 }}
        padding={{ top: 10 }}
      />

      <ChartValueAxis>
        <ChartValueAxisItem
          name="amountAxis"
          labels={{ format: "${0:n0}" }}
          min={0}
        />
        <ChartValueAxisItem name="dealsAxis" majorUnit={1} />
      </ChartValueAxis>

      <ChartCategoryAxis>
        <ChartCategoryAxisItem
          categories={categories}
          axisCrossingValues={[0, 10]}
        />
      </ChartCategoryAxis>

      <ChartSeries>{chartSeriesItems}</ChartSeries>
    </Chart>
  );
};

RevenueChart.propTypes = {
  revenueChartData: PropTypes.objectOf(
    PropTypes.shape({
      ARRAmount: PropTypes.number.isRequired,
      ServicesAmount: PropTypes.number.isRequired,
      WeighedAmount: PropTypes.number.isRequired,
      NumberOfDeals: PropTypes.number.isRequired,
    }).isRequired
  ).isRequired,
};

export default RevenueChart;
